<template>
  <div
      :class="['mt-2 mb-0 v-input v-input--horizontal v-input--center-affix', { 'd-flex align-center flex-column': compact }]"
      role="radiogroup"
      :aria-invalid="error ? 'true' : 'false'"
  >
    <div
        :class="{
      'custom-toggle':true,
      'v-btn-group':true,
      'v-btn-group--density-compact': compact,
      'v-btn-group--density-default': !compact,
      'error': error === true,
      'compact': compact
    }"
    >
      <input
          type="radio"
          :id="idFalse"
          name="yes-no-toggle"
          value="0"
          :checked="internalValue === '0'"
          @change="onChange('0')"
          :disabled="disabled"
          :readonly="readonly"
      />
      <label :for="idFalse">{{ labelFalse }}</label>

      <input
          type="radio"
          :id="idTrue"
          name="yes-no-toggle"
          value="1"
          :checked="internalValue === '1'"
          @change="onChange('1')"
          :disabled="disabled"
          :readonly="readonly"
      />
      <label :for="idTrue">{{ labelTrue }}</label>
    </div>
    <div :class="['v-input__details', { 'invisible': !error }]" role="alert" aria-live="polite">
      <div class="v-messages">
        <div class="v-messages__message" v-for="(msg, i) in errorMessages" :key="i">{{ msg }}</div>
      </div>
    </div>
  </div>

</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  modelValue: {
    type: [String, Number, Boolean],
    default: ''
  },
  rules: {
    type: Array,
    default: () => []
  },
  compact: Boolean,
  disabled: Boolean,
  readonly: Boolean
})

const emit = defineEmits(['update:modelValue'])

const { t } = useI18n()

const internalValue = ref(props.modelValue?.toString() ?? '')

watch(() => props.modelValue, (val) => {
  internalValue.value = val?.toString() ?? ''
})

const labelTrue = computed(() => t('global.text.bool.true', 'Yes'))
const labelFalse = computed(() => t('global.text.bool.false', 'No'))

const idFalse = `yes-no-false-${Math.random().toString(36).slice(2)}`
const idTrue = `yes-no-true-${Math.random().toString(36).slice(2)}`

const errorMessages = ref([])
const error = ref(false)

async function validate() {
  errorMessages.value = []

  for (const rule of props.rules || []) {
    let result
    try {
      result = rule(internalValue.value)
      if (result instanceof Promise) {
        result = await result
      }
    } catch (e) {
      result = e.message || 'Validation error'
    }

    if (result !== true) {
      error.value = true
      errorMessages.value.push(result)
      return false
    }
  }

  error.value = false
  return true
}

function onChange(val) {
  internalValue.value = val
  emit('update:modelValue', val)
  validate()
}

defineExpose({
  validate,
  resetValidation() {
    errorMessages.value = []
    error.value = false
  }
})

</script>
<style scoped>
/* Match Vuetify styles */
.custom-toggle {
  display: inline-flex;
  border-radius: 5px;
  border: 1px solid rgba(0,0,0,0.12);
  overflow: hidden;
  font-family: var(--v-theme-font, "Roboto", sans-serif);
  text-transform: uppercase;
}

/* Hide radio buttons */
.custom-toggle input {
  display: none;
}

/* Button styling */
.custom-toggle label {
  display: flex;
  padding-right: 1.2em;
  padding-left: 1.2em;
  font-size: 1rem;
  line-height: 1.5;
  text-align: center;
  cursor: pointer;
  font-weight: 500;
  color: #444;
  justify-content: center;
  align-items: center;
  user-select: none;
}

.custom-toggle.compact label {
  padding-right: 0.8em;
  padding-left: 0.8em;
  font-size: 0.7em;
}

/* Compact mode
.custom-toggle.compact label {
  padding: 1.1em 0.8em;
  font-size: 0.7em;
}
*/
/* Left button divider */
.custom-toggle label:first-of-type {
  border-right: 1px solid rgba(0,0,0,0.12);
}

/* Selected style */
.custom-toggle input:checked + label {
  background-color: green;
  color: white;
  box-shadow: inset 0 0 5px rgba(0, 0, 0, 0.2);
}
.success {
  background-color: green;
  color: white;
}
.custom-toggle.error {
  border: 1px solid rgb(var(--v-theme-error));
  color: white;
}
.v-messages__message {
  color: rgb(var(--v-theme-error));
}
.invisible {
  visibility: hidden;
}
</style>
